import { FormSchemaDropdownInput } from "#src/batteries-included-components/Dropdowns/FormSchemaDropdownInput";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  Accordion,
  ButtonToggleGroup,
  DropdownInput,
  Form,
  Skeleton,
  useForm,
  VariableTextInput,
} from "@validereinc/common-components";
import { FormSubmissionStatus } from "@validereinc/domain";
import { produce } from "immer";
import React, { useEffect, useMemo, useState } from "react";
import { useWorkflowGraphState } from "../../../../Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
import WorkflowConfigurationBaseInformation from "./WorkflowConfigurationBaseInformation";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { WorkflowConfigurationPluginOptions } from "./WorkflowConfigurationPluginOptions";

const formSubmissionStatusOptions = [
  { label: "Validated", value: FormSubmissionStatus.VALIDATED },
  { label: "Invalidated", value: FormSubmissionStatus.INVALIDATED },
  { label: "Pending", value: FormSubmissionStatus.PENDING },
  { label: "Submitted", value: FormSubmissionStatus.SUBMITTED },
];

const WorkflowConfigurationFormCreationDetails = ({
  selectedNodeId,
}: {
  selectedNodeId: string;
}) => {
  const { getAvailableStepVariables } = useWorkflowGraphState();
  const {
    setFormCreation,
    setFormCreationIsValid,
    setInput,
    getFormCreationFormValues,
    getInput,
  } = useWorkflowConfigurationFormStore();

  const availableVariables = useMemo(
    () => getAvailableStepVariables(selectedNodeId),
    [selectedNodeId]
  );

  const [activeTab, setActiveTab] = useState<
    "details" | "questions" | "advanced"
  >("details");

  const storeFormCreationValues = getFormCreationFormValues();

  const overviewForm = useForm({
    values: storeFormCreationValues ?? {
      form_schema_id: "",
      form_status: FormSubmissionStatus.PENDING,
    },
  });

  const selectedFormSchemaId = overviewForm.watch("form_schema_id");

  const formSchemaQuery = useGetOneFormSchema(
    { id: selectedFormSchemaId ?? "" },
    { enabled: !!selectedFormSchemaId }
  );

  const formSchema = formSchemaQuery.data?.data;

  const storeInputValues = getInput();

  const questionIdToValueMap = useMemo(() => {
    if (!storeInputValues) return {};

    const transformed: Record<string, string> = {};

    Object.entries(storeInputValues).forEach(([key, value]) => {
      // Extract question ID from keys like "$.questions.q1.default_answer"
      const match = key.match(/\$\.questions\.([^.]+)\.default_answer/);
      if (match) {
        const questionId = match[1];
        transformed[questionId] = value;
      }
    });

    return transformed;
  }, [JSON.stringify(storeInputValues)]);

  const dynamicFields = useMemo(() => {
    const fields: Record<string, { label: string }> = {};

    Object.entries(formSchema?.config.questions ?? {}).forEach(
      ([questionId, questionDefinition]) => {
        fields[questionId] = { label: questionDefinition.prompt };
      }
    );

    return fields;
  }, [formSchema]);

  const questionsForm = useForm({
    values: questionIdToValueMap,
  });

  // Watch specific form fields to avoid infinite re-renders
  const [formSchemaId, formStatus] = overviewForm.watch([
    "form_schema_id",
    "form_status",
  ]);

  const questionFormValues = questionsForm.watch();

  const hasShortAnswers = useMemo(
    () =>
      Object.values(questionFormValues).some(
        (value) =>
          !!value &&
          typeof value === "string" &&
          value.length > 0 &&
          value.length < 3
      ),
    [questionFormValues]
  );

  useEffect(() => {
    const formCreationValues = {
      form_schema_id: formSchemaId,
      form_status: formStatus,
    };
    setFormCreation(formCreationValues);
  }, [formSchemaId, formStatus, setFormCreation]);

  useEffect(() => {
    setFormCreationIsValid(
      Boolean(formSchemaId && formStatus && !hasShortAnswers)
    );
  }, [formSchemaId, formStatus, hasShortAnswers, setFormCreationIsValid]);

  const onQuestionAnswerChange = (questionId: string, value: string) => {
    const inputKey = `$.questions.${questionId}.default_answer`;
    setInput(
      produce(storeInputValues ?? {}, (draft) => {
        if (!value) {
          delete draft[inputKey];
        } else {
          draft[inputKey] = value;
        }
      })
    );
  };

  const [isPluginEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:plugins",
  });
  const contentSwitcherItems = useMemo(() => {
    const output: Array<{
      label: string;
      dataKey: string;
    }> = [
      { label: "Details", dataKey: "details" },
      { label: "Questions", dataKey: "questions" },
    ];

    if (isPluginEnabled) {
      output.push({ label: "Advanced", dataKey: "advanced" });
    }

    return output;
  }, [isPluginEnabled]);

  return (
    <>
      <ButtonToggleGroup
        name="content-switcher"
        items={contentSwitcherItems}
        onChange={(value) =>
          setActiveTab(value as "details" | "questions" | "advanced")
        }
        activeKey={activeTab}
      />
      {activeTab === "details" && (
        <Accordion defaultActiveKeys={["overview", "form_details"]}>
          <Accordion.AccordionPanel
            dataKey="overview"
            title="Overview"
          >
            <WorkflowConfigurationBaseInformation
              stepId={selectedNodeId}
              isStepSystemAction
            />
          </Accordion.AccordionPanel>
          <Accordion.AccordionPanel
            dataKey="form_details"
            title="Form Details"
          >
            <Form {...overviewForm}>
              <FormSchemaDropdownInput
                name="form_schema_id"
                label="Template"
                isMulti={false}
                fetchFilters={{ status: "active" }}
                isRequired
              />
              <DropdownInput
                label="Status"
                name="form_status"
                options={formSubmissionStatusOptions}
                labelKey="label"
                valueKey="value"
                isRequired
                isSearchable={false}
                isClearable={false}
              />
            </Form>
          </Accordion.AccordionPanel>
        </Accordion>
      )}
      {activeTab === "questions" && (
        <Skeleton
          isLoading={formSchemaQuery.isLoading}
          style={{ height: "100px" }}
        >
          <Form {...questionsForm}>
            {Object.entries(dynamicFields).map(([questionId, field]) => (
              <VariableTextInput
                key={questionId}
                name={questionId}
                label={field.label}
                suggestions={availableVariables}
                isRequired={false}
                isOptionalTextShown={false}
                onChange={(value) => onQuestionAnswerChange(questionId, value)}
              />
            ))}
          </Form>
        </Skeleton>
      )}
      {activeTab === "advanced" && (
        <Accordion defaultActiveKeys={["plugin"]}>
          <Accordion.AccordionPanel
            title="Plugin"
            dataKey="plugin"
          >
            <WorkflowConfigurationPluginOptions />
          </Accordion.AccordionPanel>
        </Accordion>
      )}
    </>
  );
};

export default WorkflowConfigurationFormCreationDetails;
